// modified from https://mui.com/material-ui/react-table/#sorting-amp-selecting
import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useVisibilityChange } from "@uidotdev/usehooks";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';

import { callApi, formatFieldName } from 'utils';

const defaultStyle = Object.freeze({
  minWidth: 650,
  '& th,td': {
    padding: 1,
    whiteSpace: 'pre-line',
  }
});

const defaultFormat = (row, col) => row[col];

function getComparator(orderBy) {
  let rev = 1;
  if (orderBy[0] === '-') {
    rev = -1;
    orderBy = orderBy.substring(1);
  }
  return (a, b) => rev * (a[orderBy] - b[orderBy]);
}

function EnhancedTableHead({columns, selected, onSelectAll, orderBy, onRequestSort}) {
  let orderCol, order;
  if (orderBy) {
    if (orderBy[0] === '-') {
      order = 'desc';
      orderCol = orderBy.slice(1);
    } else {
      order = 'asc';
      orderCol = orderBy;
    }
  }

  return (
    <TableHead>
      <TableRow>
      {selected !== 'disallow' && (
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={selected === 'part'}
            checked={selected === 'all'}
            onChange={onSelectAll}
          />
        </TableCell>
      )}
      {columns.map((col) => (
        <TableCell
          key={col.name}
          align={col.numeric ? 'right' : 'center'}
          padding={col.noPadding ? 'none' : 'normal'}
          sortDirection={orderCol === col.name ? order : false}
          sx={col.sx}
        >
        {col.sortable ? (
          <TableSortLabel
            active={orderCol === col.name}
            direction={orderCol === col.name ? order : 'asc'}
            onClick={() => onRequestSort(col.name)}
          >
            {col.label}
          </TableSortLabel>
        ) : (
          col.label
        )}
        </TableCell>
      ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.oneOf(['all', 'part', 'none', 'disallow']).isRequired,
  onSelectAll: PropTypes.func,
  orderBy: PropTypes.string,
  onRequestSort: PropTypes.func,
};

const EnhancedTableRow = memo(({row, columns, idColumn, selected, onSelect, dense}) => (
  <TableRow hover key={row[idColumn]}>
  {typeof selected === 'boolean' && (
    <TableCell padding="checkbox">
      <Checkbox
        value={row[idColumn]}
        size={dense ? 'small' : 'medium'}
        checked={selected}
        onChange={onSelect}
      />
    </TableCell>
  )}
  {columns.map(col => (
    <TableCell key={col.name} align={col.numeric ? 'right' : 'center'} sx={col.sx}>
      {col.format(row, col.name)}
    </TableCell>
  ))}
  </TableRow>
));

EnhancedTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  idColumn: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func,
  dense: PropTypes.bool,
};

function EnhancedTableToolbar(props) {
  const { numSelected } = props;
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          Nutrition
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

function loopLoadData(url, qs, cb, refresh, active) {
  //console.log("load", url, qs, refresh, active);
  if (!active || !url) return;

  if (qs) url += (url.indexOf('?') > 0 ? '&' : '?') + qs;
  const loadData = () => callApi(url).then(cb).catch(console.error);
  loadData();

  if (refresh <= 0) return;

  const itvl = setInterval(() => loadData(), refresh*1000);
  return () => clearInterval(itvl);
}

// columns structure:
// [{
//   name: 'abc',              // corresponding to data row field
//   label: 'Abc',             // display name in table header
//   numeric: bool,            // whether this column is numeric
//   sortable: bool,           // whether this column can be sortby
//   format: (row, col) => {}, // format column data, default to row[col]
// }, ...]
//
// data structure:
// {
//   rows: [{
//     id: 3,  // each row must have the idColumn (default 'id')
//     ...
//   }, ...],
//   total: 123,  // must have if multiPage is true
//   columns: ['col1', 'col2', ...],  // only set when display dynamic columns
// }
const EnhancedTable = ({
  columns,
  idColumn,
  dataUrl,
  dataAmend,
  refresh,
  multiPage,
  updatePageUrl,
  dense,
  defaultOrderBy,
  onDataChange,
  selected,
  onSelectedChange,
  styles,
  ...others
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const docVisible = useVisibilityChange();
  const [data, setData] = useState({rows:[]});

  // use only when updatePageUrl is false
  const [pagination, setPagination] = useState({page: 0, limit: 100, order: defaultOrderBy});

  const finalStyle = { ...defaultStyle, ...styles };
  const {rows, total, columns: dynCols} = data || {};
  const nrows = rows.length;

  const defaultFilter = {page: 0, limit: 100, order: defaultOrderBy};
  const filter = Object.fromEntries(
    ['page', 'order'].map(k => [k, (updatePageUrl ? searchParams.get(k) : pagination[k]) || defaultFilter[k]])
  );
  filter.limit = defaultFilter.limit;
  const orderBy = filter.order;

  const updateSearchParam = (k, v) => {
    if (v === filter[k]) return;
    if (updatePageUrl) {
      setSearchParams((prev) => {
        v !== defaultFilter[k] ? prev.set(k, v) : prev.delete(k);
        k !== 'page' && prev.delete('page');
        return prev;
      });
    } else {
      setPagination((prev) => ({...prev, page: 0, [k]: v}));
    }
  };

  let qs = '';
  // only set pagination and order in url if is multi-page
  if (multiPage) {
    qs = Object.entries(filter)
      .reduce((pv, [k, v]) => {v && pv.set(k, v); return pv}, new URLSearchParams())
      .toString();
  }

  let active = docVisible;
  // always active if not auto-refresh
  if (refresh <= 0)
    active = true;

  // reset page if data url changed, the parent component is in charge of the reset if updatePageUrl is true
  useEffect(() => void(updatePageUrl || setPagination(prev => ({...prev, page: 0}))), [updatePageUrl, dataUrl]);
  useEffect(() => {
    if (!updatePageUrl || !searchParams.has('limit')) return;
    setSearchParams((prev) => {
      prev.delete('limit');
      return prev;
    });
  }, [searchParams, setSearchParams, updatePageUrl]);
  // load the data once or repeating
  useEffect(() => loopLoadData(dataUrl, qs, setData, refresh, active), [dataUrl, qs, refresh, active]);
  // caution: onDataChange should be wrapped by useCallback when using
  useEffect(() => void(onDataChange?.(data)), [data, onDataChange]);
  // clear selected when fech new rows
  useEffect(() => void(onSelectedChange?.([])), [dataUrl, qs, onSelectedChange]);

  const handleRequestSort = (col) => {
    const order = orderBy === '-'+col ? '' : '-';
    updateSearchParam('order', order + col);
  };

  const multiSelect = Array.isArray(selected);

  const handleSelectAll = useCallback((ev) => {
    const newSelected = ev.target.checked ? rows.map((n) => n[idColumn]) : [];
    onSelectedChange?.(newSelected);
  }, [rows, idColumn, onSelectedChange]);
  const handleSelect = useCallback((ev) => onSelectedChange?.(ev), [onSelectedChange]);

  const cols = useMemo(() => (dynCols || columns).map((col) => {
    if (typeof col === 'string') {
      col = columns.find(a => a.name === col) || {name: col};
    }
    col.label ??= formatFieldName(col.name);
    col.format ??= defaultFormat;
    return col;
  }), [columns, dynCols]);

  const visibleRows = useMemo(() => {
    const rows_ = dataAmend ? rows.map(row => ({...row, ...dataAmend[row[idColumn]]})) : [...rows];
    // multiPage data should be sorted on server side
    return multiPage ? rows_ : rows_.sort(getComparator(orderBy));
  }, [rows, dataAmend, idColumn, multiPage, orderBy]);

  if (!cols.length) {
    return (<Box>No data</Box>);
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer sx={{overflowX: "initial"}}>
        <Table
          sx={finalStyle}
          size={dense ? 'small' : 'medium'}
          {...others}
        >
          <EnhancedTableHead
            columns={cols}
            selected={multiSelect ? (selected.length ? (selected.length < rows.length ? 'part' : 'all') : 'none') : 'disallow'}
            orderBy={orderBy}
            onSelectAll={handleSelectAll}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
          {!nrows && (
            <TableRow><TableCell colSpan={cols.length + (multiSelect|0)} sx={{textAlign: 'center'}}>No data</TableCell></TableRow>
          )}
          {visibleRows.map((row) => (
            <EnhancedTableRow
              row={row}
              columns={cols}
              idColumn={idColumn}
              selected={multiSelect ? selected.includes(row[idColumn]) : null}
              onSelect={handleSelect}
              dense={dense}
            />
          ))}
          </TableBody>
        </Table>
      </TableContainer>
    {!!multiPage && (
      <TablePagination
        component="div"
        count={total || 0}
        onPageChange={(_, page) => updateSearchParam('page', page)}
        onRowsPerPageChange={(ev) => updateSearchParam('limit', ev.target.value)}
        page={filter.page|0}
        rowsPerPage={filter.limit|0}
        rowsPerPageOptions={[100]}
      />
    )}
    </Box>
  );
};

EnhancedTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  idColumn: PropTypes.string,
  dataUrl: PropTypes.string,
  // amend data from outside, e.g. {23: {status: 'error'}}
  dataAmend: PropTypes.object,
  // seconds to auto refresh data if set
  // can also set a different negative number to reload data once
  refresh: PropTypes.number,
  // whether to show pagination
  multiPage: PropTypes.bool,
  // update browser url if pagination or order changed
  updatePageUrl: PropTypes.bool,
  dense: PropTypes.bool,
  defaultOrderBy: PropTypes.string,
  // caution: onDataChange should be wrapped by useCallback when using
  onDataChange: PropTypes.func,
  // set to null or undefined to disallow multi-select
  selected: PropTypes.array,
  onSelectedChange: PropTypes.func,
  styles: PropTypes.object,
};

EnhancedTable.defaultProps = {
  columns: [],
  idColumn: 'id',
  refresh: 0,
  updatePageUrl: false,
  dense: true,
  defaultOrderBy: '',
};

export default EnhancedTable;
